import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Upload, RefreshCw, Sparkles, ShieldAlert, Mail, ScanFace, X } from "lucide-react";
import { SectionLabel } from "@/components/Reveal";
import DemographicsFields from "@/components/DemographicsFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  "English",
  "Hindi",
  "Bengali",
  "Marathi",
  "Telugu",
  "Tamil",
  "Gujarati",
  "Kannada",
  "Malayalam",
  "Punjabi",
  "Odia",
  "Assamese",
  "Urdu",
];

// ---- image helpers ----------------------------------------------------------
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function processDataUrl(dataUrl) {
  const img = await loadImage(dataUrl);
  const maxDim = 720;
  let { width, height } = img;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  // Quality metrics on a small grayscale sample
  const s = 180;
  const qc = document.createElement("canvas");
  qc.width = s;
  qc.height = s;
  const qctx = qc.getContext("2d");
  qctx.drawImage(img, 0, 0, s, s);
  const { data } = qctx.getImageData(0, 0, s, s);
  const gray = new Float32Array(s * s);
  let sum = 0;
  for (let i = 0; i < s * s; i++) {
    const r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
    const v = 0.299 * r + 0.587 * g + 0.114 * b;
    gray[i] = v;
    sum += v;
  }
  const brightness = sum / (s * s);
  // Laplacian variance (blur)
  let lapSum = 0, lapSq = 0, n = 0;
  for (let y = 1; y < s - 1; y++) {
    for (let x = 1; x < s - 1; x++) {
      const idx = y * s + x;
      const lap = 4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - s] - gray[idx + s];
      lapSum += lap;
      lapSq += lap * lap;
      n++;
    }
  }
  const mean = lapSum / n;
  const blurVar = lapSq / n - mean * mean;

  return {
    dataUrl: canvas.toDataURL("image/jpeg", 0.82),
    brightness,
    blurVar,
  };
}

function qualityIssue({ brightness, blurVar }) {
  if (brightness < 42) return "Photo looks too dark — try again in brighter, even light.";
  if (brightness > 245) return "Photo looks overexposed — reduce direct light and retake.";
  if (blurVar < 45) return "Photo looks blurry — hold steady and retake.";
  return null;
}

// ---- UI --------------------------------------------------------------------
function GlowRing({ score }) {
  const R = 76;
  const C = 2 * Math.PI * R;
  const [val, setVal] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setVal(score), 200);
    return () => clearTimeout(t);
  }, [score]);
  return (
    <div className="relative w-[180px] h-[180px]" data-testid="skin-glow-score">
      <svg viewBox="0 0 180 180" className="w-full h-full -rotate-90">
        <circle cx="90" cy="90" r={R} fill="none" stroke="#e5d9c5" strokeWidth="10" />
        <circle
          cx="90" cy="90" r={R} fill="none" stroke="url(#gg)" strokeWidth="10"
          strokeLinecap="round" strokeDasharray={C}
          strokeDashoffset={C - (val / 100) * C}
          style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(0.16,1,0.3,1)" }}
        />
        <defs>
          <linearGradient id="gg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#c5a059" />
            <stop offset="100%" stopColor="#a9863f" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-serif text-5xl text-[color:var(--relumia-ink)]">{score}</div>
        <div className="overline mt-1">Glow Score</div>
      </div>
    </div>
  );
}

function MetricBar({ m, i }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(m.score), 250 + i * 90);
    return () => clearTimeout(t);
  }, [m.score, i]);
  return (
    <div data-testid={`skin-metric-${m.key}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[13px] text-[color:var(--relumia-ink)]">{m.label}</span>
        <span className="font-serif text-lg text-gold">{m.score}</span>
      </div>
      <div className="h-1.5 rounded-full bg-champagne overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${w}%`,
            background: "linear-gradient(to right,#c5a059,#a9863f)",
            transition: "width 1.1s cubic-bezier(0.16,1,0.3,1)",
          }}
        />
      </div>
      {m.note && <p className="text-[11.5px] text-[color:var(--relumia-muted)] mt-1.5 leading-snug">{m.note}</p>}
    </div>
  );
}

export default function SkinAnalysis() {
  const [stage, setStage] = useState("idle"); // idle | camera | preview | loading | result
  const [preview, setPreview] = useState(null); // processed dataUrl
  const [result, setResult] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [demo, setDemo] = useState({ gender: "", age: "", city: "", location: "" });
  const [language, setLanguage] = useState("English");
  const [view, setView] = useState("en"); // en | loc
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };
  useEffect(() => () => stopCamera(), []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      setStage("camera");
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 60);
    } catch (e) {
      toast.error("Couldn't access the camera. You can upload a photo instead.");
    }
  };

  const ingest = async (dataUrl) => {
    try {
      const q = await processDataUrl(dataUrl);
      const issue = qualityIssue(q);
      if (issue) {
        toast.error(issue);
        return false;
      }
      setPreview(q.dataUrl);
      setStage("preview");
      return true;
    } catch {
      toast.error("Couldn't read that image. Please try another.");
      return false;
    }
  };

  const capture = async () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    stopCamera();
    await ingest(dataUrl);
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(jpeg|jpg|png|webp)/.test(file.type)) {
      toast.error("Please upload a JPG, PNG or WEBP image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => ingest(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const analyze = async () => {
    if (!preview) return;
    if (name.trim().length < 2) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (phone.replace(/\D/g, "").length < 7) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    setStage("loading");
    try {
      const { data } = await axios.post(`${API}/skin-analysis`, {
        image: preview,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        language,
        gender: demo.gender || null,
        age: demo.age ? Number(demo.age) : null,
        city: demo.city || null,
        location: demo.location || null,
      });
      if (!data.valid) {
        toast.error(data.issue || "We couldn't read your skin clearly. Please retake.");
        setStage("preview");
        return;
      }
      setResult(data);
      setView("en");
      setStage("result");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail ||
        (status === 429 ? "Analysis limit reached. Please try again later." : "Analysis failed. Please try a clearer, well-lit selfie.");
      toast.error(msg);
      setStage("preview");
    }
  };

  const reset = () => {
    stopCamera();
    setPreview(null);
    setResult(null);
    setName("");
    setEmail("");
    setPhone("");
    setLanguage("English");
    setDemo({ gender: "", age: "", city: "", location: "" });
    setView("en");
    setStage("idle");
  };

  const hasLoc = Boolean(result?.translation && result.translation.summary);
  const loc = view === "loc" && hasLoc ? result.translation : null;
  const D = loc
    ? {
        skin_type: loc.skin_type || result.skin_type,
        summary: loc.summary || result.summary,
        concerns: loc.concerns?.length ? loc.concerns : result.concerns,
        metrics: loc.metrics?.length ? loc.metrics : result.metrics,
        routine: loc.routine?.length ? loc.routine : result.routine,
        relumia_pitch: loc.relumia_pitch || result.relumia_pitch,
      }
    : result;

  return (
    <section id="skin-ai" data-testid="skin-ai-section" className="relative z-10 bg-surface border-y border-champagne py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-[0.42fr_0.58fr] gap-10 lg:gap-16 items-start">
          {/* Intro */}
          <div className="lg:sticky lg:top-24">
            <SectionLabel>AI Skin Studio</SectionLabel>
            <h2 className="font-serif font-light tracking-tight leading-[1.03] text-4xl md:text-5xl lg:text-[3.4rem] text-[color:var(--relumia-ink)]">
              Analyse your <span className="italic text-gold">glow</span> in seconds.
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[color:var(--relumia-muted)] max-w-md">
              Take or upload a selfie and our AI reads cosmetic skin signals —
              radiance, hydration, texture, even-tone, pores and fine lines — then
              builds a personalised Relumia glow routine.
            </p>
            <div className="mt-6 flex items-start gap-3 rounded-sm bg-base border border-champagne p-4 max-w-md">
              <ShieldAlert size={18} className="text-gold shrink-0 mt-0.5" strokeWidth={1.6} />
              <p className="text-[12px] leading-relaxed text-[color:var(--relumia-muted)]">
                For educational &amp; cosmetic guidance only — not a medical
                diagnosis. Your photo is analysed to generate the report and is not
                shown publicly. Consult a dermatologist for medical concerns.
              </p>
            </div>
          </div>

          {/* Panel */}
          <div className="bg-base border border-champagne rounded-sm p-6 md:p-9 min-h-[420px]">
            {stage === "idle" && (
              <div className="flex flex-col items-center text-center py-8" data-testid="skin-idle">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-surface border border-champagne mb-6">
                  <ScanFace size={34} className="text-gold" strokeWidth={1.3} />
                </div>
                <h3 className="font-serif text-2xl text-[color:var(--relumia-ink)]">Start your skin analysis</h3>
                <p className="text-[13.5px] text-[color:var(--relumia-muted)] mt-2 max-w-sm">
                  Face the camera in soft, even light. No makeup gives the most accurate read.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full max-w-md">
                  <button
                    onClick={startCamera}
                    data-testid="skin-start-camera"
                    className="flex-1 inline-flex items-center justify-center gap-2.5 rounded-full bg-[color:var(--relumia-ink)] px-6 py-3.5 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors"
                  >
                    <Camera size={17} strokeWidth={1.8} /> Take a selfie
                  </button>
                  <button
                    onClick={() => fileRef.current?.click()}
                    data-testid="skin-upload-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2.5 rounded-full border border-[color:var(--relumia-ink)]/20 px-6 py-3.5 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:border-[color:var(--relumia-gold)] transition-colors"
                  >
                    <Upload size={17} strokeWidth={1.8} /> Upload photo
                  </button>
                </div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onUpload} className="hidden" data-testid="skin-upload-input" />
              </div>
            )}

            {stage === "camera" && (
              <div className="flex flex-col items-center" data-testid="skin-camera">
                <div className="relative w-full max-w-[420px] aspect-square rounded-sm overflow-hidden bg-black">
                  <video ref={videoRef} playsInline muted className="w-full h-full object-cover -scale-x-100" />
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-[62%] h-[80%] rounded-[50%] border-2 border-white/70 border-dashed" />
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <button onClick={capture} data-testid="skin-capture-btn" className="inline-flex items-center gap-2.5 rounded-full bg-[color:var(--relumia-ink)] px-7 py-3.5 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors">
                    <Camera size={17} strokeWidth={1.8} /> Capture
                  </button>
                  <button onClick={reset} className="inline-flex items-center gap-2 rounded-full border border-[color:var(--relumia-ink)]/20 px-6 py-3.5 text-[13px] text-[color:var(--relumia-ink)] hover:border-[color:var(--relumia-gold)] transition-colors">
                    <X size={16} /> Cancel
                  </button>
                </div>
              </div>
            )}

            {stage === "preview" && preview && (
              <div className="flex flex-col items-center" data-testid="skin-preview">
                <div className="w-full max-w-[380px] aspect-square rounded-sm overflow-hidden border border-champagne">
                  <img src={preview} alt="Selfie preview" className="w-full h-full object-cover" />
                </div>
                <p className="mt-6 text-[12.5px] text-[color:var(--relumia-muted)] text-center max-w-[380px]">
                  Enter your details to receive your personalised Glow report by email.
                </p>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name*"
                  data-testid="skin-name-input"
                  className="mt-4 w-full max-w-[380px] bg-transparent border-b border-[color:var(--relumia-ink)]/20 py-2.5 text-[14px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)]/70 focus:outline-none focus:border-[color:var(--relumia-gold)]"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email*"
                  data-testid="skin-email-input"
                  className="mt-3 w-full max-w-[380px] bg-transparent border-b border-[color:var(--relumia-ink)]/20 py-2.5 text-[14px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)]/70 focus:outline-none focus:border-[color:var(--relumia-gold)]"
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Your mobile number*"
                  data-testid="skin-phone-input"
                  className="mt-3 w-full max-w-[380px] bg-transparent border-b border-[color:var(--relumia-ink)]/20 py-2.5 text-[14px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)]/70 focus:outline-none focus:border-[color:var(--relumia-gold)]"
                />
                <div className="mt-4 w-full max-w-[380px]">
                  <label className="overline block mb-2">Report language (English + selected)</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    data-testid="skin-language-select"
                    className="w-full bg-base border border-champagne rounded-sm py-2.5 px-3 text-[14px] text-[color:var(--relumia-ink)] focus:outline-none focus:border-[color:var(--relumia-gold)]"
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l} value={l}>
                        {l === "English" ? "English only" : `English + ${l}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-full max-w-[380px]">
                  <DemographicsFields values={demo} onChange={setDemo} tidPrefix="skin-demo" />
                </div>
                <div className="mt-6 flex gap-3">
                  <button onClick={analyze} data-testid="skin-analyze-btn" className="inline-flex items-center gap-2.5 rounded-full bg-[color:var(--relumia-ink)] px-8 py-3.5 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors">
                    <Sparkles size={16} strokeWidth={1.8} /> Analyse my skin
                  </button>
                  <button onClick={reset} data-testid="skin-retake-btn" className="inline-flex items-center gap-2 rounded-full border border-[color:var(--relumia-ink)]/20 px-6 py-3.5 text-[13px] text-[color:var(--relumia-ink)] hover:border-[color:var(--relumia-gold)] transition-colors">
                    <RefreshCw size={15} /> Retake
                  </button>
                </div>
              </div>
            )}

            {stage === "loading" && (
              <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="skin-loading">
                <div className="relative h-16 w-16">
                  <div className="absolute inset-0 rounded-full border-2 border-champagne" />
                  <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[color:var(--relumia-gold)] animate-spin" />
                </div>
                <p className="font-serif text-2xl text-[color:var(--relumia-ink)] mt-6">Reading your glow…</p>
                <p className="text-[13px] text-[color:var(--relumia-muted)] mt-1">Mapping zones &amp; cosmetic signals</p>
              </div>
            )}

            {stage === "result" && result && (
              <div data-testid="skin-result">
                {hasLoc && (
                  <div className="flex justify-center sm:justify-end mb-5" data-testid="skin-lang-toggle">
                    <div className="inline-flex rounded-full border border-champagne bg-surface p-1">
                      <button
                        onClick={() => setView("en")}
                        data-testid="skin-lang-en"
                        className={`px-4 py-1.5 rounded-full text-[12px] tracking-wide transition-colors ${view === "en" ? "bg-[color:var(--relumia-ink)] text-white" : "text-[color:var(--relumia-muted)]"}`}
                      >
                        English
                      </button>
                      <button
                        onClick={() => setView("loc")}
                        data-testid="skin-lang-loc"
                        className={`px-4 py-1.5 rounded-full text-[12px] tracking-wide transition-colors ${view === "loc" ? "bg-[color:var(--relumia-ink)] text-white" : "text-[color:var(--relumia-muted)]"}`}
                      >
                        {result.language}
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row items-center gap-6 pb-6 border-b border-champagne">
                  <GlowRing score={result.glow_score} />
                  <div className="text-center sm:text-left">
                    {D.skin_type && <div className="overline mb-1">{D.skin_type}</div>}
                    <p className="text-[14px] leading-relaxed text-[color:var(--relumia-ink)]">{D.summary}</p>
                    {D.concerns?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
                        {D.concerns.map((c, i) => (
                          <span key={i} className="text-[11px] rounded-full bg-surface border border-champagne px-3 py-1 text-[color:var(--relumia-muted)]">{c}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 py-7">
                  {D.metrics.map((m, i) => <MetricBar key={m.key + view} m={m} i={i} />)}
                </div>

                {D.routine?.length > 0 && (
                  <div className="border-t border-champagne pt-7">
                    <div className="overline mb-4">Your Relumia glow routine</div>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {D.routine.map((r, i) => (
                        <div key={i} className="bg-surface border border-champagne rounded-sm p-5" data-testid={`skin-routine-${i}`}>
                          <div className="font-serif text-xl text-[color:var(--relumia-ink)] mb-2">{r.step}</div>
                          <p className="text-[12.5px] leading-relaxed text-[color:var(--relumia-muted)]">{r.recommendation}</p>
                          {r.relumia && <p className="text-[12px] leading-relaxed text-gold mt-2">✦ {r.relumia}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {D.relumia_pitch && (
                  <div className="mt-6 bg-[color:var(--relumia-ink)] rounded-sm p-6 flex flex-col sm:flex-row items-center gap-5">
                    <p className="text-[13.5px] leading-relaxed text-white/85 flex-1">{D.relumia_pitch}</p>
                    <a href="#contact" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-gold)] hover:text-white transition-colors whitespace-nowrap">
                      Get Relumia →
                    </a>
                  </div>
                )}

                {/* email confirmation + reset */}
                <div className="mt-6 flex flex-col sm:flex-row items-center gap-3 justify-between">
                  <span className="text-[12.5px] text-[color:var(--relumia-muted)] inline-flex items-center gap-2" data-testid="skin-email-confirm">
                    <Mail size={14} className="text-gold" /> Your Glow report{result.language !== "English" ? ` (English + ${result.language})` : ""} has been emailed{email ? ` to ${email}` : ""}.
                  </span>
                  <button onClick={reset} data-testid="skin-again-btn" className="inline-flex items-center gap-2 text-[12.5px] text-gold hover:text-[color:var(--relumia-gold-deep)] transition-colors">
                    <RefreshCw size={14} /> Analyse another
                  </button>
                </div>

                <p className="mt-6 text-[11px] text-[color:var(--relumia-muted)] leading-relaxed">
                  For educational &amp; cosmetic guidance only. Not a medical diagnosis. Consult a dermatologist for medical concerns.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
